import { getStoreBuilder, BareActionContext } from 'vuex-typex';
import { RootState } from './index';
import { SearchResult, SelectedItemPayload } from '@/models/QuickSearchModel';
import { ApiService } from '@/services/ApiService';

export interface SearchState {
    searchResult: SearchResult['items'];
    query: string;
    selectedItem: number;
    loading: boolean;
}

const initialState: SearchState = {
    searchResult: [] as SearchResult['items'],
    query: '',
    selectedItem: -1,
    loading: false
};

const store = getStoreBuilder<RootState>().module('search', initialState);

// private getters
function getStoreSearchResult(state: SearchState): SearchResult['items'] {
    return state.searchResult;
}
function getStoreSearchQuery(state: SearchState): string {
    return state.query;
}
function getStoreSearchSelectedItem(state: SearchState): number {
    return state.selectedItem;
}

// getters
const getStateSearchResult = store.read(getStoreSearchResult, 'getStoreSearchResult');
const getStateSearchQuery = store.read(getStoreSearchQuery, 'getStoreSearchQuery');
const getStateSearchSelectedItem = store.read(getStoreSearchSelectedItem, 'getStoreSearchSelectedItem');

// mutations:
function mutateSearchResult(state: SearchState, payload: string): Promise<void> {
    return new Promise((resolve, reject) => {
        state.loading = true;
        state.query = payload;
        ApiService.getSearchResult(payload).then(response => {
            if (response && response.data) {
                state.searchResult = response.data;
            }
            state.loading = false;
            resolve();
        });
    });
}
function mutateSearchSelectedItem(state: SearchState, event: KeyboardEvent, searchUrl: string) {
    if (!state.searchResult || !state.searchResult.length) {
        return;
    }

    const max = state.searchResult.length - 1,
        clip = (index: number) => (index < 0 ? max : index > max ? 0 : index);
    switch (event.key) {
        case 'ArrowUp':
            state.selectedItem = clip(state.selectedItem - 1);
            break;
        case 'ArrowDown':
            state.selectedItem = clip(state.selectedItem + 1);
            break;
        case 'Enter': {
            const selectedObject = state.searchResult[state.selectedItem];
            if (selectedObject && selectedObject.url) {
                if (selectedObject.target === '_blank') window.open(selectedObject.url, '_blank');
                else location.href = selectedObject.url;
            } else {
                location.href = searchUrl;
            }
            break;
        }
        default:
            break;
    }
}

// actions
async function saveSearchResult(context: BareActionContext<SearchState, RootState>, payload: string): Promise<void> {
    return await mutateSearchResult(context.state, payload);
}
function saveSearchSelectedItem(
    context: BareActionContext<SearchState, RootState>,
    payload: SelectedItemPayload
): void {
    return mutateSearchSelectedItem(context.state, payload.event, payload.searchUrl);
}

// export store
export const SearchStore = {
    get searchResult() {
        return getStateSearchResult();
    },
    get searchQuery() {
        return getStateSearchQuery();
    },
    get searchSelectedItem() {
        return getStateSearchSelectedItem();
    },
    saveSearchResult: store.dispatch(saveSearchResult, 'saveSearchResult'),
    saveSearchSelectedItem: store.dispatch(saveSearchSelectedItem, 'saveSearchSelectedItem')
};
